'use client';

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { previewAnnotationTargetsEqual } from '@/lib/preview/annotation';
import type { ImageAttachment } from '@/lib/research-assistant/types';
import type {
  PreviewAnnotationStroke,
  PreviewAnnotationTarget,
  PreviewSnapshot,
} from '@/lib/preview/annotation';

export interface PreviewAnnotationDraft {
  id: string;
  target: PreviewAnnotationTarget;
  snapshot: PreviewSnapshot;
  strokes: PreviewAnnotationStroke[];
  images: ImageAttachment[] | null;
  error?: string;
}

const PreviewAnnotationContext = createContext<{
  scopeKey: string;
  draft: PreviewAnnotationDraft | null;
  setDraft: (draft: PreviewAnnotationDraft) => void;
  clearDraft: (expectedId?: string, expectedDraft?: PreviewAnnotationDraft) => void;
}>({ scopeKey: '', draft: null, setDraft: () => {}, clearDraft: () => {} });

/** Image bytes stay in ephemeral composer state, outside persisted browser awareness. */
export function PreviewAnnotationProvider({
  scopeKey,
  target,
  targetResetKey = '',
  children,
}: {
  scopeKey: string;
  target?: PreviewAnnotationTarget | null;
  targetResetKey?: string;
  children: ReactNode;
}) {
  const [entry, setEntry] = useState<{
    scopeKey: string;
    targetResetKey?: string;
    draft: PreviewAnnotationDraft;
  } | null>(null);
  const bindingRef = useRef({ scopeKey, target, targetResetKey });
  bindingRef.current = { scopeKey, target, targetResetKey };
  const valid = Boolean(
    entry &&
      entry.scopeKey === scopeKey &&
      (entry.targetResetKey || '') === targetResetKey &&
      (target === undefined ||
        (target && previewAnnotationTargetsEqual(entry.draft.target, target)))
  );
  // Forget an old scope before committing children, including when its editor is unmounted.
  if (entry && !valid) setEntry(null);
  const draft = valid ? entry!.draft : null;
  const setDraft = useCallback(
    (next: PreviewAnnotationDraft) => {
      const binding = bindingRef.current;
      if (
        binding.scopeKey !== scopeKey ||
        binding.targetResetKey !== targetResetKey ||
        (binding.target !== undefined &&
          (!binding.target || !previewAnnotationTargetsEqual(next.target, binding.target)))
      )
        return;
      setEntry({ scopeKey, targetResetKey, draft: next });
    },
    [scopeKey, targetResetKey]
  );
  const clearDraft = useCallback((expectedId?: string, expectedDraft?: PreviewAnnotationDraft) => {
    setEntry(current =>
      (!expectedId || current?.draft.id === expectedId) &&
      (!expectedDraft || current?.draft === expectedDraft)
        ? null
        : current
    );
  }, []);
  const value = useMemo(
    () => ({ scopeKey, draft, setDraft, clearDraft }),
    [scopeKey, draft, setDraft, clearDraft]
  );
  return (
    <PreviewAnnotationContext.Provider value={value}>{children}</PreviewAnnotationContext.Provider>
  );
}

export function usePreviewAnnotationAwareness() {
  return useContext(PreviewAnnotationContext);
}
